import { NonCancelableCustomEvent } from "@amzn/awsui-components-react/polaris/internal/events/index";
import TimeInput, {
  TimeInputProps,
} from "@amzn/awsui-components-react/polaris/time-input";
import { useField, useFormikContext } from "formik";
import React, { useCallback } from "react";

import { FormikFieldProps } from "./../FormControls.interface";
import memoizedFormikPropCheck, {
  isFieldInvalid,
} from "./../FormControls.util";

export interface TimeInputFieldProps<S> {
  name: keyof S;
  timeInputProps: Omit<TimeInputProps, "value" | "onBlur" | "onChange">;
  placeholder: string;
  didSubmit?: boolean;
}

const TimeInputField = <S, T extends string>({
  timeInputProps,
  placeholder,
  field,
  meta,
  helpers,
  didSubmit,
}: TimeInputFieldProps<S> & FormikFieldProps<T>) => {
  const handleBlur = useCallback(() => {
    /**
     * @jcortezj 1/14/2022
     * Setting a timeout of 0 allows the next tab to focus before
     * helpers.setTouched gets called.
     *
     * Without the setTimeout, if you press tab in some context (e.g. in a modal) it does not
     * focus to the next form element correctly.
     *
     * Validated by @parkyh
     */
    setTimeout(() => {
      helpers.setTouched(true);
    }, 0);
  }, [helpers]);

  const onChange = useCallback(
    (e: NonCancelableCustomEvent<TimeInputProps.ChangeDetail>) => {
      helpers.setValue(e.detail.value as T);
    },
    [helpers]
  );

  return (
    <TimeInput
      data-testid="time-input-field"
      onBlur={handleBlur}
      onChange={onChange}
      value={field.value}
      invalid={isFieldInvalid<T>({ meta }, didSubmit)}
      placeholder={placeholder}
      {...timeInputProps}
    />
  );
};

const MemoizedTimeInputField = React.memo(
  TimeInputField,
  (prevProps, nextProps) => {
    if (
      !memoizedFormikPropCheck(prevProps, nextProps) ||
      prevProps.timeInputProps.disabled !== nextProps.timeInputProps.disabled
    ) {
      return false;
    }

    return true;
  }
);

const TimeInputFieldContainer = <S,>(props: TimeInputFieldProps<S>) => {
  const [field, meta, helpers] = useField<string>(props.name as string);
  const { submitCount } = useFormikContext();

  const optProps = {
    ...props,
    name: props.name as never,
    didSubmit: submitCount >= 0,
  };

  return <MemoizedTimeInputField {...{ field, meta, helpers }} {...optProps} />;
};

export default TimeInputFieldContainer;
